import * as React from 'react'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import xor from 'lodash/xor'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DeprecatedText from 'modules/core/components/Inputs/Text'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import legalEntities from 'modules/core/store/collections/legalEntities'

import LegalEntitySelector from 'modules/settings/components/LegalEntitySelector'
import genders from 'modules/settings/store/collections/genders'
import legalGenders from 'modules/settings/store/collections/legalGenders'

interface Props {
  onClose: () => void
}

class SchemaForm extends Form {
  constructor(values: object) {
    super(values, {
      name: 'string',
    })
  }
}

const CreateGender = ({ onClose }: Props) => {
  const form = React.useRef(new SchemaForm({})).current
  const [selectedIds, setSelectedIds] = React.useState<string[]>([])

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const { success } = await asyncAction(async () => {
      const { id } = await form.create(genders)
      Promise.all(
        selectedIds.map((legalEntityId) => {
          legalGenders.create({
            gender_id: id,
            legal_entity_id: legalEntityId,
          })
        })
      )
    })

    if (success) closePortal()
  }

  const onSelectMultiple = (legalEntityIds: string[]) => {
    setSelectedIds(legalEntityIds)
  }

  const onSelect = (legalEntityId: string | null | undefined) => {
    if (!legalEntityId) return setSelectedIds([])

    setSelectedIds(xor(selectedIds, [legalEntityId]))
  }

  const disabled = !form.get('name').value || selectedIds.length === 0

  return (
    <Modal
      illustration="input-field"
      title={i18n.t('gender_settings.edit.title')}
      description={i18n.t('gender_settings.edit.description')}
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('gender_settings.edit.cancel')}
          </Button>
          <Button disabled={disabled} submit>
            {i18n.t('gender_settings.edit.accept')}
          </Button>
        </>
      }
    >
      <FormRow>
        <DeprecatedText
          label={i18n.t('gender_settings.edit.name_field')}
          field={form.get('name')}
        />
      </FormRow>
      <FormRow>
        <Box gap="s4">
          <Box>
            <Text.Normal size="200" weight="medium" color="grey1000">
              {i18n.t('gender_settings.edit.legal_entities_field')}
            </Text.Normal>
          </Box>
          <LegalEntitySelector
            onSelect={onSelect}
            onSelectMultiple={onSelectMultiple}
            selectedIds={selectedIds}
            legalEntities={legalEntities.models}
          />
        </Box>
      </FormRow>
    </Modal>
  )
}

export default observer(CreateGender)
